import React, { useState, useCallback, useMemo } from 'react';
import {
    Container, Box, Typography, Paper, TextField, FormControl, FormControlLabel,
    Button, Alert, CircularProgress,
    MenuItem, Select, InputLabel, SelectChangeEvent, TableContainer, Table,
    TableHead, TableRow, TableCell, TableBody, Card, Checkbox
} from '@mui/material';
import { InssObraInputs, InssObraResult, calculateInssObra } from '../services/inssCalculator';

interface Option {
    value: string;
    label: string;
}

const destinacaoOptions: Option[] = [
    { value: "Residencial Unifamiliar", label: "Residencial Unifamiliar" },
    { value: "Residencial Multifamiliar", label: "Residencial Multifamiliar" },
    { value: "Comercial Salas e Lojas", label: "Comercial Salas e Lojas" },
    { value: "Galpão Industrial", label: "Galpão Industrial" },
    { value: "Casa Popular", label: "Casa Popular" },
    { value: "Edifício de Garagens", label: "Edifício de Garagens" },
    { value: "Comercial Bancos", label: "Comercial Bancos" },
    { value: "Comercial Hotéis", label: "Comercial Hotéis" },
    { value: "Serviços de Saúde", label: "Serviços de Saúde" },
    { value: "Templos", label: "Templos" },
    { value: "Escolas", label: "Escolas" },
    { value: "Clubes e Centros Esportivos", label: "Clubes e Centros Esportivos" },
    { value: "Obras Complementares", label: "Obras Complementares" },
];

const categoriaOptions: Option[] = [
    { value: "Obra Nova", label: "Obra Nova" },
    { value: "Acréscimo", label: "Acréscimo" },
    { value: "Reforma", label: "Reforma" },
    { value: "Demolição", label: "Demolição" },
];

const tipoOptions: Option[] = [
    { value: "Alvenaria", label: "Alvenaria" },
    { value: "Madeira", label: "Madeira" },
    { value: "Mista", label: "Mista" },
];

const InssObraPage: React.FC = () => {
    const [inputs, setInputs] = useState<InssObraInputs>({
        vau: 0,
        areaPrincipal: '' as any,
        areaComplementarCoberta: '' as any,
        areaComplementarDescoberta: '' as any,
        destinacao: "Residencial Unifamiliar",
        categoria: "Obra Nova",
        tipo: "Alvenaria",
        pjOptanteSimples: false,
    });
    const [vauDisplay, setVauDisplay] = useState<string>(''); // Estado para exibição formatada
    const [result, setResult] = useState<InssObraResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setInputs(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) : value)
        }));
    };

    const handleVauChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Remove tudo exceto dígitos
        const numericValue = value.replace(/\D/g, '');

        if (numericValue === '') {
            setVauDisplay('');
            setInputs(prev => ({ ...prev, vau: 0 }));
            return;
        }

        // Converte para número (centavos)
        const numberValue = parseFloat(numericValue) / 100;

        // Formata para exibição
        const formatted = numberValue.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        });

        setVauDisplay(formatted);
        setInputs(prev => ({ ...prev, vau: numberValue }));
    };

    const handleSelectChange = (e: SelectChangeEvent<string>) => {
        const { name, value } = e.target;
        setInputs(prev => ({ ...prev, [name]: value }));
    };

    const handleCalculate = useCallback(() => {
        setError(null);
        setLoading(true);
        setResult(null);
        try {
            // Basic validation
            if (inputs.vau <= 0 || inputs.areaPrincipal <= 0) {
                throw new Error("VAU e Área Principal são campos obrigatórios e devem ser maiores que zero.");
            }
            if (inputs.areaComplementarCoberta < 0 || inputs.areaComplementarDescoberta < 0) {
                throw new Error("Áreas complementares não podem ser negativas.");
            }

            const calculationResult = calculateInssObra(inputs);
            setResult(calculationResult);
        } catch (err: any) {
            setError(err.message || "Ocorreu um erro ao calcular o INSS da Obra.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [inputs]);

    const formatNumber = (num: number | null | undefined, decimals: number = 2): string => {
        if (num === null || num === undefined) {
            return '-'; // Retorna '-' para valores nulos/indefinidos
        }
        return num.toLocaleString('pt-BR', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    };

    // Exibir o resultado ou campos opcionais apenas se calculoBase estiver disponível
    const showCalculoBase = useMemo(() => result && result.calculoBase, [result]);

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>Calculadora de INSS de Obra</Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <FormControl fullWidth margin="normal">
                    <TextField
                        label="VAU (Valor Atualizado Unitário)"
                        type="text"
                        name="vau"
                        value={vauDisplay}
                        onChange={handleVauChange}
                        fullWidth
                        required
                        placeholder="R$ 0,00"
                    />
                </FormControl>
                <FormControl fullWidth margin="normal">
                    <TextField
                        label="Área Principal (m²)"
                        type="number"
                        name="areaPrincipal"
                        value={inputs.areaPrincipal || ''}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        inputProps={{ step: "0.01" }}
                    />
                </FormControl>
                <FormControl fullWidth margin="normal">
                    <TextField
                        label="Área Complementar Coberta (m²)"
                        type="number"
                        name="areaComplementarCoberta"
                        value={inputs.areaComplementarCoberta || ''}
                        onChange={handleInputChange}
                        fullWidth
                        inputProps={{ step: "0.01" }}
                    />
                </FormControl>
                <FormControl fullWidth margin="normal">
                    <TextField
                        label="Área Complementar Descoberta (m²)"
                        type="number"
                        name="areaComplementarDescoberta"
                        value={inputs.areaComplementarDescoberta || ''}
                        onChange={handleInputChange}
                        fullWidth
                        inputProps={{ step: "0.01" }}
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel id="destinacao-label">Destinação</InputLabel>
                    <Select
                        labelId="destinacao-label"
                        id="destinacao"
                        name="destinacao"
                        value={inputs.destinacao}
                        onChange={handleSelectChange}
                        label="Destinação"
                    >
                        {destinacaoOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel id="categoria-label">Categoria</InputLabel>
                    <Select
                        labelId="categoria-label"
                        id="categoria"
                        name="categoria"
                        value={inputs.categoria}
                        onChange={handleSelectChange}
                        label="Categoria"
                    >
                        {categoriaOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel id="tipo-label">Tipo de Construção</InputLabel>
                    <Select
                        labelId="tipo-label"
                        id="tipo"
                        name="tipo"
                        value={inputs.tipo}
                        onChange={handleSelectChange}
                        label="Tipo de Construção"
                    >
                        {tipoOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={inputs.pjOptanteSimples}
                            onChange={handleInputChange}
                            name="pjOptanteSimples"
                            color="primary"
                        />
                    }
                    label="Pessoa Jurídica Optante pelo Simples Nacional"
                    sx={{ mt: 2 }}
                />

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCalculate}
                    disabled={loading}
                    sx={{ mt: 3 }}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                    {loading ? "Calculando..." : "Calcular INSS"}
                </Button>
            </Paper>

            {result && (
                <Paper sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h5" gutterBottom>Resultado do Cálculo</Typography>

                    {/* Inputs Usados */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2 }}>Inputs Utilizados</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold' }}>VAU:</TableCell><TableCell>{formatNumber(result.inputs.vau)}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold' }}>Área Principal:</TableCell><TableCell>{formatNumber(result.inputs.areaPrincipal)} m²</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold' }}>Área Comp. Coberta:</TableCell><TableCell>{formatNumber(result.inputs.areaComplementarCoberta)} m²</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold' }}>Área Comp. Descoberta:</TableCell><TableCell>{formatNumber(result.inputs.areaComplementarDescoberta)} m²</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold' }}>Área Total:</TableCell><TableCell>{formatNumber(result.inputs.areaTotal)} m²</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold' }}>Destinação:</TableCell><TableCell>{result.inputs.destinacao}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold' }}>Categoria:</TableCell><TableCell>{result.inputs.categoria}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold' }}>Tipo:</TableCell><TableCell>{result.inputs.tipo}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold' }}>PJ Optante Simples:</TableCell><TableCell>{result.inputs.pjOptanteSimples ? 'Sim' : 'Não'}</TableCell></TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* Cálculo Base */}
                    {showCalculoBase && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2 }}>Cálculo Base</Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableBody>
                                        <TableRow><TableCell sx={{ fontWeight: 'bold' }}>VAU Aplicado:</TableCell><TableCell>{formatNumber(result.calculoBase.vauAplicado)}</TableCell></TableRow>
                                        <TableRow><TableCell sx={{ fontWeight: 'bold' }}>Custo da Obra Calculado (COD):</TableCell><TableCell>{formatNumber(result.calculoBase.custoObraCalculado)}</TableCell></TableRow>
                                        <TableRow><TableCell sx={{ fontWeight: 'bold' }}>RMT Base Aferição:</TableCell><TableCell>{formatNumber(result.calculoBase.rmtBaseAfericao)}</TableCell></TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {/* Comparativo de Débitos */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2 }}>Comparativo de Débitos</Typography>
                        <TableContainer component={Card} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Cenário</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>RMT Calculada / Mínima</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Alíquota</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Débito Aferição (Sero)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {/* PJ */}
                                    <TableRow>
                                        <TableCell>{result.comparativoDebitos.pessoaJuridica.descricao}</TableCell>
                                        <TableCell align="right">{formatNumber(result.comparativoDebitos.pessoaJuridica.rmtCalculada)}</TableCell>
                                        <TableCell align="right">{formatNumber(result.comparativoDebitos.pessoaJuridica.aliquotaAplicada, 3)}</TableCell>
                                        <TableCell align="right">{formatNumber(result.comparativoDebitos.pessoaJuridica.debitoAfericaoSero)}</TableCell>
                                    </TableRow>
                                    {/* PF Fator Social */}
                                    <TableRow>
                                        <TableCell>{result.comparativoDebitos.pessoaFisicaFatorSocial.descricao}</TableCell>
                                        <TableCell align="right">{formatNumber(result.comparativoDebitos.pessoaFisicaFatorSocial.rmtCalculada)}</TableCell>
                                        <TableCell align="right">{formatNumber(result.comparativoDebitos.pessoaFisicaFatorSocial.aliquotaAplicada, 3)}</TableCell>
                                        <TableCell align="right">{formatNumber(result.comparativoDebitos.pessoaFisicaFatorSocial.debitoAfericaoSero)}</TableCell>
                                    </TableRow>
                                    {/* PF Fator Ajuste */}
                                    <TableRow>
                                        <TableCell>{result.comparativoDebitos.pessoaFisicaFatorAjuste.descricao}</TableCell>
                                        <TableCell align="right">{formatNumber(result.comparativoDebitos.pessoaFisicaFatorAjuste.rmtMinimaNecessaria)}</TableCell>
                                        <TableCell align="right">{formatNumber(result.comparativoDebitos.pessoaFisicaFatorAjuste.aliquotaAplicada, 3)}</TableCell>
                                        <TableCell align="right">{formatNumber(result.comparativoDebitos.pessoaFisicaFatorAjuste.debitoMinimoRecolhido)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* Observações */}
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2 }}>Observações</Typography>
                        <ul>
                            {result.observacoes.map((obs, index) => (
                                <Typography component="li" key={index} variant="body2" color="text.secondary">{obs}</Typography>
                            ))}
                        </ul>
                    </Box>
                </Paper>
            )}
        </Container>
    );
};

export default InssObraPage;